import React, { ReactNode, useState } from 'react';
import { Link, To } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, BoxSection } from 'components/_new/Box';
import toolsIcon from 'assets/tools.svg';
import knowledgePillsIcon from 'assets/knowledge-pills.svg';
import guideIcon from 'assets/guide.svg';
import formsIcon from 'assets/forms.svg';
import { RemoveDirectoryDialog } from 'components/programPanel/knowledgeBase/components/RemoveDirectoryDialog';
import { ModifyDirectoryDialog } from 'components/programPanel/knowledgeBase/components/ModifyDirectoryDialog';
import { Button } from 'components/_new/Button';
import { classNames } from 'primereact/utils';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { TopCardsSkeleton } from './skeletons/TopCardsSkeleton';

type Item = {
	key: string;
	id: number | null;
	name: string;
	link: To;
	visualData: {
		svg: string;
		hoverColor: string;
	};
	isSystem: boolean;
	canDelete: boolean;
	articles: number;
};

type DirectoryCardsProps = {
	homepageMode?: boolean;
	additionalCards?: Array<{ key: string; name: string; link: To; icon?: number | ReactNode | (() => ReactNode) }>;
	renderRight?: ReactNode | ((data: Item) => ReactNode);
};

export const DirectoryCards = ({ homepageMode = false, additionalCards, renderRight }: DirectoryCardsProps) => {
	const { t } = useTranslation();

	const { directories, directoriesQuery, generateDirectoryLink, context } = useKnowledgeBase();
	const { isLoading } = directoriesQuery;

	const getCategoryVisualData = (directoryId: number, iconNumber?: number) => {
		switch (iconNumber || directoryId) {
			case 1:
				return { svg: knowledgePillsIcon, hoverColor: '#2ecc71' };
			case 2:
				return { svg: toolsIcon, hoverColor: '#f1c40f' };
			case 4:
				return { svg: formsIcon, hoverColor: '#e74c3c' };
			case 5:
			case 3:
			case 6:
			default:
				return { svg: guideIcon, hoverColor: '#3498db' };
		}
	};

	const topCategories: Item[] = [
		...[...directories.filter(({ info }) => info.isSystem), ...directories.filter(({ info }) => !info.isSystem)]
			.filter(({ info }) => (homepageMode ? info.displayInHomepage === true : true))
			.map(({ id, name, slug, info }) => {
				const visualData = getCategoryVisualData(id);
				const link = generateDirectoryLink(id, slug);
				const { isSystem, articles } = info;
				const canDelete = !isSystem && articles === 0;
				return {
					key: `card-${id}`,
					id,
					name,
					link,
					visualData,
					isSystem,
					canDelete: ['program-admin', 'coordinator'].includes(context) && canDelete,
					articles,
				};
			}),
		...(additionalCards || []).map(({ key, name, link, icon }) => {
			return {
				key,
				id: null,
				name,
				link,
				visualData: getCategoryVisualData(typeof icon === 'number' ? icon : 0),
				isSystem: true,
				canDelete: false,
				articles: 0,
			};
		}),
	];

	const [editDirectoryDialogOpen, setEditDirectoryDialogOpen] = useState(false);
	const [removeDirectoryDialogOpen, setRemoveDirectoryDialogOpen] = useState(false);
	const [selectedDirectory, setSelectedDirectory] = useState<number | null>(null);

	if (isLoading) {
		return <TopCardsSkeleton />;
	}

	return (
		<>
			{['program-admin', 'coordinator'].includes(context) && (
				<>
					<RemoveDirectoryDialog
						directoryId={selectedDirectory}
						visible={removeDirectoryDialogOpen}
						onHide={() => {
							setSelectedDirectory(null);
							setRemoveDirectoryDialogOpen(false);
						}}
					/>
					<ModifyDirectoryDialog
						directoryId={selectedDirectory}
						visible={editDirectoryDialogOpen}
						onHide={() => {
							setSelectedDirectory(null);
							setEditDirectoryDialogOpen(false);
						}}
					/>
				</>
			)}

			{!topCategories?.length ? (
				<div className="text-muted text-center">{t('misc.empty')}</div>
			) : (
				<div className="grid">
					{topCategories.map((item) => {
						const { key, id, name, link, visualData, isSystem, canDelete, articles } = item;
						return (
							<Link
								key={key}
								to={link}
								className={classNames('knowledge-base-card-link flex flex-column col-12', {
									'sm:col-6 md:col-4': !homepageMode,
									'md:col-6': homepageMode,
								})}
								onClick={() => {
									setTimeout(() => {
										document.body.scrollTop = 0;
										document.getElementById('scrollWrapperRef')?.scrollTo({ top: 0 });
										document.getElementById('pageScrollWrapperRef')?.scrollTo({ top: 0 });
									}, 100);
								}}
							>
								<Box
									wrap
									hoverable
									slim={homepageMode}
									variant="white-bordered"
									className={classNames('knowedge-base-box knowledge-base-box h-full', {
										'box-directory-link': homepageMode,
									})}
								>
									<BoxSection
										horizontal
										contentClassName={classNames('h-full justify-content-start', {
											'align-items-start': ['program-admin', 'coordinator'].includes(context),
											'align-items-center': context === 'user-panel',
										})}
									>
										{visualData?.svg && (
											<div className="flex justify-content-start">
												<div className="knowedge-base-box-icon">
													<div className="circle-inbg" />
													<img
														src={visualData.svg}
														alt={name}
														className={classNames('relative z-1', {
															'w-3rem h-3rem': !homepageMode,
															'w-1rem h-1rem': homepageMode,
														})}
													/>
												</div>
											</div>
										)}

										<div className="flex-1 px-2">
											<div className="font-semibold">{name}</div>

											{['program-admin', 'coordinator'].includes(context) && (
												<>
													<div className="text-sm text-muted mt-2">
														{t('knowledgeBase.programPanel.articlesCount')}: {articles}
													</div>

													{isSystem ? (
														<div className="text-xs text-muted">
															{t('knowledgeBase.programPanel.directorySystem')}
														</div>
													) : (
														<div className="flex gap-2 mt-2">
															<Button
																label={t('actions.edit')}
																icon="pencil"
																onClick={(event) => {
																	event.stopPropagation();
																	event.preventDefault();
																	setSelectedDirectory(id);
																	setEditDirectoryDialogOpen(true);
																}}
																variant="primary-outlined"
																variantSize="sm"
															/>
															<Button
																label={t('actions.remove')}
																icon="trash"
																onClick={(event) => {
																	event.stopPropagation();
																	event.preventDefault();
																	setSelectedDirectory(id);
																	setRemoveDirectoryDialogOpen(true);
																}}
																variant="danger-text"
																variantSize="sm"
																disabled={!canDelete}
																tooltip={
																	!canDelete
																		? t(
																				'knowledgeBase.programPanel.canNotDeleteWhenContainsArticles'
																		  )
																		: undefined
																}
															/>
														</div>
													)}
												</>
											)}
										</div>

										{typeof renderRight === 'function' ? renderRight(item) : renderRight || null}
									</BoxSection>
								</Box>
							</Link>
						);
					})}
				</div>
			)}
		</>
	);
};
